import { Integration, LocalEditableIntegration } from "../../../Everou/_modelsTS/Integration";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIntegrationName } from "./useIntegrationName";
import { CapsuleButton, CustomNumberInput } from "../../../Everou/_components";
import { useDispatch, useSelector } from "react-redux";
import { MIN_TIME } from "../../../Everou/_constants";
import { useError } from "../../../Everou/_hooks/useError";
import { Icons } from "../../../Everou/_assets";
import SendInvitationURLToggle from "../../../Everou/IntegrationsModule/components/SendInvitationURLToggle";
import { PMSIntegrationsCollection } from "../../_stores/PMSIntegrationsCollection";
import { PMSIntegrationsActions } from "../../_actions/PMSIntegrationsActions";
import { CheckInOutFields } from "../../../Everou/IntegrationsModule/components/CheckInOutFields";
import { IntegrationSectionTitle } from "../../../Everou/IntegrationsModule/components/IntegrationSectionTitle";
import ToolTipWrapper from "../../../Everou/_components/ToolTipWrapper";


type IntegrationInputProps = {
    integration: LocalEditableIntegration;
    onChangeInputs?: (...args: any) => void | any;
    onToggleEditable: (...args: any) => void | any;
    onDelete: (index: number) => void;
    index: number;
}

export function PMSAvaibookNewIntegrationInput({ 
    integration, 
    onDelete, 
    index,
    onToggleEditable 
}: IntegrationInputProps) {

    const { t } = useTranslation();
    const { templateName, nameInput } = useIntegrationName(integration);
    const dispatch = useDispatch();
    
    const integrations = useSelector(PMSIntegrationsCollection.selectorGetItems);

    const baseIntegration = useMemo(() => integration, [integration]);
    const [apikeyValue, setApikeyValue] = useState(integration.apikey ?? '');
    const [sendInvitationURL, setSendInvitationURL] = useState(integration.sendInvitationURL ?? true);
    const [daysForInvitationCreate, setDaysForInvitationCreate] = useState(integration.daysForInvitationCreate ?? 1);
    const { errorMessage, setError, hasError, disableError } = useError();

    const [checkIn, setCheckIn] = useState(integration.check_in ?? MIN_TIME);
    const [checkOut, setCheckOut] = useState(integration.check_out ?? MIN_TIME);

    useEffect(() => { 
        setApikeyValue(integration.apikey ?? '');
        setSendInvitationURL(integration.sendInvitationURL ?? true);
        setDaysForInvitationCreate(integration.daysForInvitationCreate ?? 1);
        setCheckIn(integration.check_in ?? '00:00');
        setCheckOut(integration.check_out ?? '00:00');
    }, [integration]);

    const canSave = useMemo(() => {

        if(hasError) return false;

        return (
            !!apikeyValue && 
            (
                !integration.uid ||
                apikeyValue !== baseIntegration.apikey ||
                integration.createPublicUrl !== true ||
                sendInvitationURL !== integration.sendInvitationURL || 
                daysForInvitationCreate !== integration.daysForInvitationCreate ||
                checkIn !== integration.check_in ||
                checkOut !== integration.check_out
            )
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        integration.uid,
        baseIntegration.link, 
        integrations, 
        integration.createPublicUrl, 
        apikeyValue, 
        sendInvitationURL, 
        daysForInvitationCreate,
        checkIn, 
        checkOut
    ]);
    

    const onSave = useCallback(async () => {

        const editedIntegration: Integration = {
            name: templateName,
            apikey: apikeyValue,
            company: integration.company,
            // Not user-configurable for Avaibook: the whole delivery (online check-in
            // register-access-data + email/SMS) depends on the public link, and all 60
            // Avaibook integrations in prod run with it on. Same policy as Hostify.
            createPublicUrl: true,
            uid: integration.uid,
            sendInvitationURL,
            daysForInvitationCreate,
            check_in: checkIn,
            check_out: checkOut
        }
        try {
            await dispatch(PMSIntegrationsActions.createIntegration(editedIntegration));
        } catch (err: any) {
            setError(err.message)
        }
    }, [
        dispatch,
        templateName, 
        setError, 
        integration.company,
        integration.uid, 
        apikeyValue,
        t, 
        sendInvitationURL,
        daysForInvitationCreate,
        checkIn, 
        checkOut
    ])

    return (
        <div className="c-grid c-grid--small w-100 c-hover-250 p-4 rounded-lg">
            {nameInput}
            <IntegrationSectionTitle title={t("mod_integrations_section_credentials")} />
            <div className="col-span-full">
                <label className="font-weight-bold mb-1 d-block">Token</label>
                <input
                    className={`${hasError ? 'input-error' : ''} ${!integration.editable ? 'cna' : ''} w-100 p-2 c-h-min c-input-bordered`}
                    type="text"
                    readOnly={!integration.editable}
                    value={apikeyValue}
                    placeholder={"Token"}
                    onChange={(e) => {
                        setApikeyValue(e.target.value?.trim());
                        /*if(IntegrationsHelper.checkIfLinkExists(e.target.value, integrations) && e.target.value !== integration.link) {
                            setError(t('mod_integrations_already_have_integration_with_this_link'));
                        } else {
                            disableError();
                        }*/
                    }}
                />
            </div>
            <div className="col-span-full mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_delivery")} />
            </div>
            <div className="col-span-full">
                <SendInvitationURLToggle
                    optional
                    boldedName={false}
                    initialState={sendInvitationURL}
                    readOnly={!integration.editable}
                    onChange={setSendInvitationURL}
                />
                <div className="d-flex align-items-start c-bg-grey-250 c-rounded-big p-2 mt-2">
                    <img
                        src={Icons.info}
                        alt="info"
                        className="mr-2 flex-shrink-0"
                        style={{ width: "18px", height: "18px" }}
                    />
                    <span className="small text-muted">{t("mod_integrations_avaibook_access_data_info")}</span>
                </div>
            </div>
            <div className="col-span-full mt-4">
                <IntegrationSectionTitle title={t("mod_integrations_section_schedule")} />
            </div>
            <CheckInOutFields
                checkIn={checkIn}
                checkOut={checkOut}
                onChangeCheckIn={setCheckIn}
                onChangeCheckOut={setCheckOut}
                readOnly={!integration.editable}
            />
            <div className="col-span-full d-flex align-items-center justify-content-between">
                <ToolTipWrapper tooltip={t("mod_integrations_days_for_invitation_create_tooltip")}>
                    <label className="font-weight-bold mb-0 mr-2">{t("mod_integrations_days_for_invitation_create")}</label>
                </ToolTipWrapper>
                <CustomNumberInput
                    value={daysForInvitationCreate}
                    min={1}
                    max={30}
                    readOnly={!integration.editable}
                    onChange={setDaysForInvitationCreate}
                />
            </div>
            <div className="col-span-full mt-4 d-flex justify-content-between align-items-center">
                <div className="d-flex">
                    {integration.uid &&
                        <CapsuleButton
                            size="SMALL"
                            classNameExtra="mr-2"
                            style={!integration.editable ? 'PRIMARY' : 'INFO'}
                            onClick={() => onToggleEditable(index)}
                            text={t(!integration.editable ?  "global_edit" : 'global_cancel')}
                        />
                    }
                    {integration.uid &&
                        <CapsuleButton
                            onClick={() => onDelete(index)}
                            classNameExtra="mr-2"
                            // eslint-disable-next-line react/style-prop-object
                            style="DANGER"
                            size="SMALL"
                            icon={Icons.trash}
                        />
                    }
                </div>
                <CapsuleButton
                    size="SMALL"
                    isEnabled={canSave}
                    onClick={onSave}
                    text={t("global_save")}
                    />

            </div>
            {errorMessage && (
                <div className="text-danger mr-2">{errorMessage}</div>
            )}
        </div>
    );
}